<template>
  <div>
    <h5 class="ticket-no">Ticket No. #{{ ticket.ticketNumber }}</h5>
    <h5 class="text-primary">{{ ticket.subject }}</h5>
    <span class="ticket-date text-muted">
      <img
        :src="require('@/assets/images/icon-ticket_time.svg')"
        alt="Number"
      />
      {{ ticketDate(ticket.createdTime) }}
    </span>
    <div class="d-flex align--center justify--center">
      <button class="btn btn-outline-primary rounded btn-micro" type="button">
        Department: {{ ticket.department }}
      </button>
      <button
        class="btn rounded btn-micro mx-2"
        :class="
          ticket.status == 'Open'
            ? 'btn-outline-success'
            : ticket.status == 'Closed'
            ? 'btn-outline-danger'
            : 'btn-outline-warning'
        "
        type="button"
      >
        Status: {{ ticket.status }}
      </button>
      <button
        class="btn btn-outline-primary rounded btn-micro mx-2"
        type="button"
      >
        Priority: {{ ticket.priority }}
      </button>
      <button
        class="btn btn-outline-primary rounded btn-micro mx-2"
        type="button"
      >
        Channel: {{ ticket.channel }}
      </button>
      <a
        class="btn btn-primary rounded btn-micro ml-auto"
        target="_blank"
        :href="`https://${ticket.replyUrl}`"
      >
        Reply
      </a>
    </div>
    <br />
    <hr />
    <div class="ticket-details">
      <div class="ticket-details_header">
        <div class="ticket-img">
          <img src="@/assets/images/avatar.png" alt="Profile" />
        </div>
        <div>
          <h4 class="username" v-if="currentUser">{{ currentUser.name }}</h4>
          <span class="ticket-date text-muted">
            {{ ticketDate(ticket.createdTime) }}
          </span>
        </div>
      </div>
      <div class="ticket-content" v-html="ticket.description"></div>
    </div>
    <div class="ticket-replies" v-if="ticket.replies.length > 0">
      <h5>Ticket replies</h5>
      <hr />
      <div
        class="ticket-details"
        v-for="(reply, index) in ticket.replies"
        :key="reply.id || index"
      >
        <div class="ticket-details_header">
          <div class="ticket-img">
            <img src="@/assets/images/avatar.png" alt="Profile" />
          </div>
          <div>
            <h4 class="username">{{ reply.authorName }}</h4>
            <span class="ticket-date text-muted">
              {{ ticketDate(reply.createdTime) }}
            </span>
          </div>
        </div>
        <div
          class="ticket-content"
          @click="onContentClick"
          v-html="isExpanded(reply) ? fullContent[reply.id] : reply.summary"
        ></div>
        <div v-if="reply.id" class="ticket-content pt-0">
          <button
            class="btn btn-link btn-sm p-0"
            type="button"
            :disabled="loadingThread[reply.id]"
            @click="toggleReply(reply)"
          >
            <i
              v-if="loadingThread[reply.id]"
              class="fa fa-spinner fa-spin mr-1"
            ></i>
            {{ isExpanded(reply) ? 'Show less' : 'Show more' }}
          </button>
        </div>
      </div>
    </div>
    <button class="btn btn-primary btn-sm rounded" @click="$emit('go-back')">
      <div class="btn-with-icon-content">
        <i class="fa fa-arrow-left"></i>
        Back
      </div>
    </button>

    <div v-if="zoomSrc" class="image-lightbox" @click="closeZoom">
      <button
        type="button"
        class="image-lightbox__close"
        aria-label="Close"
        @click="closeZoom"
      >
        <i class="fa fa-times"></i>
      </button>
      <img :src="zoomSrc" alt="" @click.stop />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
const moment = require('moment')

export default {
  name: 'ViewTicket',
  props: ['ticket'],
  data() {
    return {
      loadingTicket: false,
      expanded: {}, // reply.id -> bool
      fullContent: {}, // reply.id -> full HTML content
      loadingThread: {}, // reply.id -> bool
      zoomSrc: null // src of the inline image being zoomed
    }
  },
  computed: {
    ...mapGetters(['currentUser'])
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    ...mapActions(['fetchTicketThread']),
    ticketDate(date) {
      return moment(date).calendar()
    },
    // Delegated: open the lightbox when an inline image inside the reply is clicked.
    onContentClick(e) {
      if (e.target && e.target.tagName === 'IMG') {
        this.zoomSrc = e.target.src
      }
    },
    closeZoom() {
      this.zoomSrc = null
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.closeZoom()
    },
    isExpanded(reply) {
      return !!(
        reply.id &&
        this.expanded[reply.id] &&
        this.fullContent[reply.id]
      )
    },
    async toggleReply(reply) {
      if (!reply.id) return

      // Collapse if already open.
      if (this.expanded[reply.id]) {
        this.$set(this.expanded, reply.id, false)
        return
      }

      // Serve cached full content instantly.
      if (this.fullContent[reply.id]) {
        this.$set(this.expanded, reply.id, true)
        return
      }

      // The list only carries a truncated summary — fetch the full body by id.
      this.$set(this.loadingThread, reply.id, true)
      try {
        const res = await this.fetchTicketThread({
          ticketId: this.ticket.id,
          threadId: reply.id
        })
        if (res && res.success) {
          this.$set(this.fullContent, reply.id, res.data.content)
          this.$set(this.expanded, reply.id, true)
        }
      } finally {
        this.$set(this.loadingThread, reply.id, false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
h5 {
  margin-top: 0;
  font-size: 1.2rem;
}
.ticket-date {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  img {
    max-height: 16px;
    max-width: 31px;
    margin-right: 10px;
  }
}
.ticket-details {
  .ticket-details_header {
    display: flex;
    align-items: center;
    .ticket-img {
      width: 60px;
      height: 60px;
      // border: 2px solid $blue;
      border-radius: 50%;
      margin-right: 20px;
      img {
        width: 100%;
      }
    }
    .username {
      margin: 0;
      font-size: 1.5rem;
      text-transform: capitalize;
    }
    .ticket-date {
      margin-bottom: 0;
    }
  }
  .ticket-content {
    padding: 30px 80px;
    ::v-deep img {
      max-width: 100%;
      height: auto;
      cursor: zoom-in;
      border-radius: 4px;
    }
  }
}
.ticket-replies {
  margin-top: 1rem;
  .ticket-details {
    padding-left: 60px;
  }
}
.image-lightbox {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.8);
  cursor: zoom-out;
  img {
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
    border-radius: 4px;
  }
  &__close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: transparent;
    border: 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.5rem;
    cursor: pointer;
    &:hover {
      color: #fff;
    }
  }
}
</style>
